import type { BondRenderField } from "@/lib/bondFields";

/**
 * "Please complete:" and the fields a blocked save still needs.
 *
 * Its own module since BRK-S181, because it renders in two places now: at step
 * level for the fields that belong to no one card, and INSIDE an indemnitor
 * card for the fields that belong to that person — one component, so the two
 * cannot come to word the same refusal differently.
 *
 * BRK-S319 — each item is a LINK that takes the broker to the field, and says
 * WHERE it is ("Date of birth — Indemnitor, primary owner"). Daniel's walk
 * read an owner's missing date of birth as the spouse's, because the list sat
 * beside the spouse form and named only the label. Both are optional props:
 * a caller with nowhere to send the broker keeps the plain list.
 */
export function BondMissingChecklist({
  missing,
  placeOf,
  onGo,
  title = "Please complete:",
}: {
  missing: readonly BondRenderField[];
  /** The list's lead line. A refusal about fields on OTHER steps says so. */
  title?: string;
  /** Where the field is — step and roster card — or null to say nothing. */
  placeOf?: (field: BondRenderField) => string | null;
  /** Take the broker to the field and focus it. */
  onGo?: (field: BondRenderField) => void;
}) {
  if (missing.length === 0) return null;
  return (
    <div
      role="alert"
      className="flex flex-col gap-1 rounded-md border border-brk-danger/40 bg-brk-danger/5 px-3 py-2 text-sm text-brk-text"
    >
      <p className="font-medium">{title}</p>
      <ul className="list-inside list-disc text-brk-dim">
        {missing.map((f) => {
          const place = placeOf?.(f) ?? null;
          const text = place ? `${f.label} — ${place}` : f.label;
          return (
            <li key={f.name}>
              {onGo ? (
                <button
                  type="button"
                  onClick={() => onGo(f)}
                  className="text-left text-brk-danger underline underline-offset-2 hover:no-underline"
                >
                  {text}
                </button>
              ) : (
                text
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
}
